'use client'
import { type ReactNode, Suspense, useState } from 'react'
import { cx } from 'class-variance-authority'

import { ImpersonationBanner, MainHeader, Navbar } from '@/components/organisms'
import { InlineErrorBoundary } from '@/components/errors/InlineErrorBoundary'
import { NavbarProvider, useNavbarContext } from '@/context'
import { PendingInvitationRedirect } from '@/components/listeners/PendingInvitationRedirect'
import { OnboardingExitProvider } from '@/features/onboarding/OnboardingExitContext'
import {
  OnboardingRouteGate,
  shouldRenderFullAppShell,
} from '@/features/onboarding/OnboardingRouteGate'
import { usePathname } from '@/navigation'

// Loading skeleton component for main content
const MainContentSkeleton = () => (
  <div className="animate-pulse space-y-4">
    <div className="h-8 w-3/4 rounded bg-gray-200" />
    <div className="h-40 rounded bg-gray-200" />
    <div className="h-24 rounded bg-gray-200" />
    <div className="h-40 rounded bg-gray-200" />
  </div>
)

const OnboardingGateFallback = () => (
  <div className="min-h-[100dvh] bg-white" aria-hidden />
)

const RestrictedShellContent = ({
  children,
  modal,
}: {
  readonly children: ReactNode
  readonly modal?: ReactNode
}) => {
  const pathname = usePathname()
  const { navbarCollapsed, toggleNavbarCollapsed } = useNavbarContext()
  const [showNavbar, setShowNavbar] = useState<boolean>(false)
  const showAppShell = shouldRenderFullAppShell(pathname)

  const toggleShowNavbarAction = () => setShowNavbar((p) => !p)

  if (!showAppShell) {
    return (
      <>
        <PendingInvitationRedirect />
        <OnboardingRouteGate fallback={<OnboardingGateFallback />}>
          {children}
        </OnboardingRouteGate>
      </>
    )
  }

  return (
    <div
      className={cx(
        'app-shell h-[100dvh] w-screen overflow-x-hidden transition-[grid-template-columns] duration-300 ease-in-out md:grid',
        {
          'grid-cols-navbar': !navbarCollapsed,
          'grid-cols-navbar-collapsed': navbarCollapsed,
        },
      )}
    >
      <PendingInvitationRedirect />
      <Navbar
        collapsed={navbarCollapsed}
        toggleCollapsedAction={toggleNavbarCollapsed}
        showNavbar={showNavbar}
      />
      <div className="flex h-[100dvh] min-w-0 flex-grow flex-col bg-stone-100">
        <ImpersonationBanner />
        <MainHeader toggleShowNavbarAction={toggleShowNavbarAction} />
        <main className="relative min-h-0 flex-1 overflow-y-auto bg-stone-100 p-[var(--app-main-padding)]">
          <OnboardingRouteGate fallback={<OnboardingGateFallback />}>
            <InlineErrorBoundary label="Contenu indisponible">
              <Suspense fallback={<MainContentSkeleton />}>{children}</Suspense>
            </InlineErrorBoundary>
          </OnboardingRouteGate>
        </main>
      </div>
      {modal}
    </div>
  )
}

export default function RestrictedShell({
  children,
  modal,
  initialCollapsed,
}: {
  readonly children: React.ReactNode
  readonly modal?: React.ReactNode
  readonly initialCollapsed?: boolean
}) {
  return (
    <NavbarProvider initialCollapsed={initialCollapsed}>
      <OnboardingExitProvider>
        <RestrictedShellContent modal={modal}>
          {children}
        </RestrictedShellContent>
      </OnboardingExitProvider>
    </NavbarProvider>
  )
}
